/**
 * Defines a few helper functions to render/clean/copy Markdown text
 * Typically in the context of LLM-generated Markdown text
 */

import showdown from 'showdown'
import DOMPurify from 'dompurify'

function getMarkdownConverter () {
  return new showdown.Converter({
    disableForced4SpacesIndentedSublists: true, // Because Chat uses fewer spaces to indent lists
    simpleLineBreaks: true, // Turns all \n into <br />
    simplifiedAutoLink: true, // Make urls clickable
    openLinksInNewWindow: true, // Make all links open in a new tab
    tables: true // Render Markdown tables
  })
}

function renderMarkdown (mdConverter, mdText) {
  // Cleans and converts Markdown text to html
  const parsed = mdConverter.makeHtml(mdText)
  return DOMPurify.sanitize(parsed, { ADD_ATTR: ['target'] })
}

function cleanMarkdown (mdText) {
  // The LLM will generally respond in pure Markdown, except that we asked it to format any source-links like this:
  // <a href="https://app.academiclabs.com/search/projects?project=St9gaeWpPa8C" target="_blank" title="Source - Conference--American Society of Gene Therapy"><i class="fa fa-link ml-1"></i></a>
  // When copying/downloading the LLM response, we'll want to convert these back to simple Markdown links.
  return mdText.replace(
    /<a\s+href="([^"]+)"[^>]*title="[^"]*Source[^"]*"[^>]*>.*?<\/a>/g,
    (match, url) => `[Source](${url})`
  )
}

function cleanHtml (htmlText) {
  // Simplifies the source links, so they'll look OK when pasted into e.g. Word
  return htmlText.replace(
    /<a\s+href="([^"]+)"[^>]*title="[^"]*Source[^"]*"[^>]*>.*?<\/a>/g,
    (match, url) => `<a href="${url}">[Source]</a>`
  )
}

async function copyMdToClipboard (
  mdConverter,
  rawMdText,
  onSuccess = () => {},
  onError = () => {}
) {
  // Cleans and copies rawMdText into the clipboard,
  // both as plain text and as HTML

  const htmlText = cleanHtml(renderMarkdown(mdConverter, rawMdText))
  const mdText = cleanMarkdown(rawMdText)

  const clipboardItem = new window.ClipboardItem({
    'text/plain': new Blob([mdText], { type: 'text/plain' }),
    'text/html': new Blob([htmlText], { type: 'text/html' })
  })
  try {
    await navigator.clipboard.write([clipboardItem])
    onSuccess()
  } catch (_e) {
    onError()
  }
}

export default {
  getMarkdownConverter,
  renderMarkdown,
  cleanMarkdown,
  cleanHtml,
  copyMdToClipboard
}
